import React, { useState, useEffect } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import externalCommissionService from '../../services/ExternalCommissionService.js';
import {
  effectiveAgentCommission,
  effectiveOwnerShare,
  effectiveAgentRemit,
  effectiveOwnerPayable,
  effectiveAgentPayable,
  remainingAgentRemit,
  remainingOwnerPay,
  remainingAgentPay,
  remainingTotalPay,
  bookingAgentSummary,
} from '../../utils/externalCommissionMetrics.js';
import { Users, DollarSign, ChevronDown, ChevronRight, Search } from 'lucide-react';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const ExternalHubInsights = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [owners, setOwners] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [ownersLoading, setOwnersLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [totals, setTotals] = useState({});
  const [expandedRow, setExpandedRow] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    externalCommissionService.getBulkSettlementOwners()
      .then((res) => setOwners(res.data?.data || []))
      .catch(() => Alert.error('Failed to load vehicle owners'))
      .finally(() => setOwnersLoading(false));
  }, []);

  useEffect(() => {
    setRecords([]);
    setTotals({});
    setExpandedRow(null);
    setHasFetched(false);
  }, [selectedOwnerId]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount || 0);

  const getStatusBadge = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'PAID') return 'bg-green-100 text-green-700';
    if (s === 'PARTIAL') return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  const fetchRecords = async () => {
    if (!selectedOwnerId) {
      Alert.error('Please select a vehicle owner');
      return;
    }
    try {
      setLoading(true);
      const res = await externalCommissionService.getBulkSettlementRecords(undefined, selectedOwnerId);
      setRecords(res.data?.data || []);
      setTotals(res.data?.totals || {});
      setHasFetched(true);
      setExpandedRow(null);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load settlement records');
      setRecords([]);
      setTotals({});
      setHasFetched(true);
    } finally {
      setLoading(false);
    }
  };

  const selectedOwner = owners.find((o) => o.vehicle_owner_id === selectedOwnerId);

  const summaryCards = [
    { label: 'To Pay Freight', value: totals.balance_freight, border: 'border-orange-500' },
    { label: 'Agent Commission', value: totals.total_agent_commission, border: 'border-purple-500', valueClass: 'text-purple-700' },
    { label: 'Owner Share', value: totals.total_owner_share ?? totals.owner_commission_amount, border: 'border-green-500', valueClass: 'text-green-700' },
    { label: 'Agent Remit Due', value: totals.net_receivable, border: 'border-amber-500', valueClass: 'text-amber-700' },
    { label: 'Collected from Agent', value: totals.amount_received, border: 'border-blue-500', valueClass: 'text-blue-700' },
    { label: 'Remit Remaining', value: totals.remaining_net, border: 'border-red-500', valueClass: 'text-red-600' },
    { label: 'Pay to Owner Due', value: totals.net_payable_to_owner, border: 'border-indigo-500', valueClass: 'text-indigo-700' },
    { label: 'Pay to Agent Due', value: totals.net_payable_to_agent, border: 'border-violet-500', valueClass: 'text-violet-700' },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2">
            <Users className="w-5 h-5 text-secondary shrink-0" /> Hub Insights
          </h1>
          <Button variant="secondary" icon={DollarSign} onClick={() => navigate('/external-commissions')} className="w-full sm:w-auto">
            Back to Commission Record
          </Button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Settlement overview by vehicle owner — agent remittance, owner share, and booking-agent commission across ML sheets.
        </p>

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm mb-4 sm:mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 items-end">
            <div className="sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Vehicle Owner</label>
              {ownersLoading ? (
                <div className="w-full px-4 py-2 border border-gray-300 rounded-sm text-sm text-gray-400">Loading owners...</div>
              ) : (
                <SelectField variant="form"
                  value={selectedOwnerId}
                  onChange={(e) => setSelectedOwnerId(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm font-medium text-gray-700"
                >
                  <option value="">Select a vehicle owner...</option>
                  {owners.map((owner) => (
                    <option key={owner.vehicle_owner_id} value={owner.vehicle_owner_id}>
                      {owner.vehicle_owner_name || owner.vehicle_owner_id}
                    </option>
                  ))}
                </SelectField>
              )}
            </div>
            <div>
              <Button variant="primary" icon={Search} onClick={fetchRecords} disabled={!selectedOwnerId || loading} fullWidth>
                {loading ? 'Searching...' : t('common.search')}
              </Button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading settlement data..." />
          </div>
        )}

        {!loading && hasFetched && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
              {summaryCards.map((card) => (
                <div key={card.label} className={`bg-white rounded-sm shadow-sm p-4 border-l-4 ${card.border}`}>
                  <p className="text-xs text-gray-500 font-medium mb-1">{card.label}</p>
                  <p className={`text-xl font-bold text-secondary ${card.valueClass || ''}`}>{formatCurrency(card.value)}</p>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-800">
                  {selectedOwner?.vehicle_owner_name || selectedOwnerId} — {records.length} record{records.length !== 1 ? 's' : ''}
                </h2>
              </div>
              {records.length === 0 ? (
                <p className="text-gray-500 text-center py-10 text-sm">No settlement records for this owner</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {records.map((row) => {
                    const isExp = expandedRow === row.external_commission_id;
                    const rowRemaining = remainingAgentRemit(row) + remainingTotalPay(row);
                    const agents = row.booked_agent_commission_json?.agents || [];
                    return (
                      <div key={row.external_commission_id} className="px-4 py-3">
                        <div
                          className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-lg p-2 -mx-2"
                          onClick={() => setExpandedRow(isExp ? null : row.external_commission_id)}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {isExp ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-gray-900 truncate">{row.external_commission_id}</p>
                              <p className="text-xs text-gray-500">{row.ml_sheet_id} · {bookingAgentSummary(row)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-sm font-semibold text-orange-700">{formatCurrency(rowRemaining)}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusBadge(row.payment_status || row.settlement_status)}`}>
                              {row.payment_status || row.settlement_status || 'PENDING'}
                            </span>
                          </div>
                        </div>
                        {isExp && (
                          <div className="mt-2 ml-6 space-y-3">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                              <div className="bg-gray-50 p-2 rounded"><p className="text-gray-500">To Pay</p><p className="font-bold">{formatCurrency(row.balance_freight)}</p></div>
                              <div className="bg-purple-50 p-2 rounded"><p className="text-gray-500">Agent Comm.</p><p className="font-bold text-purple-700">{formatCurrency(effectiveAgentCommission(row))}</p></div>
                              <div className="bg-green-50 p-2 rounded"><p className="text-gray-500">Owner Share</p><p className="font-bold text-green-700">{formatCurrency(effectiveOwnerShare(row))}</p></div>
                              <div className="bg-orange-50 p-2 rounded"><p className="text-gray-500">Agent Remit</p><p className="font-bold text-orange-700">{formatCurrency(effectiveAgentRemit(row))}</p></div>
                              <div className="bg-blue-50 p-2 rounded"><p className="text-gray-500">Pay to Owner</p><p className="font-bold text-blue-700">{formatCurrency(effectiveOwnerPayable(row))}</p></div>
                              <div className="bg-violet-50 p-2 rounded"><p className="text-gray-500">Pay to Agent</p><p className="font-bold text-violet-700">{formatCurrency(effectiveAgentPayable(row))}</p></div>
                              <div className="bg-gray-50 p-2 rounded"><p className="text-gray-500">Remit Remaining</p><p className="font-bold text-red-600">{formatCurrency(remainingAgentRemit(row))}</p></div>
                              <div className="bg-gray-50 p-2 rounded"><p className="text-gray-500">Pay Remaining</p><p className="font-bold text-red-600">{formatCurrency(remainingTotalPay(row))}</p></div>
                            </div>
                            {agents.length > 0 && (
                              <div className="overflow-x-auto">
                                <table className="w-full min-w-[420px] text-xs">
                                  <thead>
                                    <tr className="bg-gray-50 text-gray-600">
                                      <th className="px-2 py-1 text-left">Agent</th>
                                      <th className="px-2 py-1 text-right">Freight</th>
                                      <th className="px-2 py-1 text-right">Commission</th>
                                      <th className="px-2 py-1 text-right">Remit</th>
                                      <th className="px-2 py-1 text-right">Owner (to-pay)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {agents.map((agent) => (
                                      <tr key={agent.agent_id} className="border-t">
                                        <td className="px-2 py-1">{agent.agent_name}</td>
                                        <td className="px-2 py-1 text-right">{formatCurrency(agent.total_freight)}</td>
                                        <td className="px-2 py-1 text-right">{formatCurrency(agent.agent_commission_amount)}</td>
                                        <td className="px-2 py-1 text-right">{formatCurrency(agent.agent_remit_to_owner)}</td>
                                        <td className="px-2 py-1 text-right">{formatCurrency(agent.owner_share_on_to_pay)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            <Button variant="link" size="sm" onClick={() => navigate(`/external-commissions/view/${row.external_commission_id}`)}>
                              View Full Details →
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ExternalHubInsights;
